import { FC, useEffect } from "react";
import { useSelector } from "react-redux";
import { Routes, BrowserRouter, Route } from "react-router-dom";

import {
  LoginPage,
  NotFoundPage,
  ResetPasswordPage,
  RecoverPasswordPage,
  SignUpPage,
} from "../pages";
import {
  selectIsPasswordChangePending,
  useLazyGetPropertiesQuery,
} from "../redux";
import { AnonymousLayout, AppLayout } from "../layouts";
import PrivateOutlet from "./PrivateOutlet";
import PublicOutlet from "./PublicOutlet";
import { PROPERTIES_URL } from "../config";

const MainRouter: FC = (): JSX.Element => {
  /**
   * Cambio obligatorio pendiente: el ingreso respondió `422` y no abrió sesión.
   * La pantalla de cambio ocupa el lugar del ingreso, que es pública, y como
   * toda ruta privada redirige ahí, nada de la aplicación es accesible hasta
   * completar el cambio o desistir — HU-CUDL-088 / CA-07.
   */
  const isPasswordChangePending = useSelector(selectIsPasswordChangePending);
  const [getProperties] = useLazyGetPropertiesQuery();

  useEffect(() => {
    /**
     * Sin `unwrap`, la consulta no rechaza: el `catch` que había aquí nunca
     * corría. Si el catálogo no llega, cada formulario lo hace visible con su
     * guarda de propiedad (hallazgo E).
     *
     * Siempre a `PROPERTIES_URL`, no a la URL que trae la copia guardada: ver
     * su comentario en `config.ts` (VWA-43).
     */
    getProperties(PROPERTIES_URL);
  }, [getProperties]);

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<PrivateOutlet />}>
          <Route index element={<AppLayout />} />
        </Route>
        <Route
          path="login"
          element={
            <PublicOutlet>
              {isPasswordChangePending ? <ResetPasswordPage /> : <LoginPage />}
            </PublicOutlet>
          }
        />
        <Route
          path="recoverPassword"
          element={
            <PublicOutlet>
              <RecoverPasswordPage />
            </PublicOutlet>
          }
        />
        <Route
          path="signUp"
          element={
            <PublicOutlet>
              <SignUpPage />
            </PublicOutlet>
          }
        />
        <Route
          path="help"
          element={
            <PublicOutlet>
              <AnonymousLayout />
            </PublicOutlet>
          }
        />
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </BrowserRouter>
  );
};

export default MainRouter;
